import { Component, Renderer2, OnInit, AfterViewInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { AuthenticationService } from '../../services/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { ModelValidators } from '../../models/validators/validators';
import { AppConfig } from '../../configs/app.config';
import { SUPPORT_EMAIL, PORTAL_NAME } from 'src/app/constants/global';
import { Subject } from 'rxjs';

declare var App: any;

@Component({
    templateUrl: './set-password.html',
    styles: [
        // Formulário
        '.login-container .login-columns { margin: 50px auto; display: flex; }',
        '.login-container .slideshow { margin: auto; width: 500px; display: inline-block; }',
        '.login-container .block-flat { margin: 0; border-radius: 0; box-shadow: none; }',
        '.login-container .login-form { margin: auto 20px auto auto; display: inline-block; }',
        '@media screen and (max-width: 965px) { .login-container .login-columns { display: block; } .login-container .login-form { display: block; margin: auto auto 20px; } .login-container .slideshow {display: block; margin: auto auto 20px; width: auto; } .width-950, .width-450 { width: auto; } }',
        '.ngucarousel > .ngu-touch-container > .ngucarousel-items > .item { padding: 0 !important; }'
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SetPasswordComponent implements OnInit, AfterViewInit, OnDestroy {
  SUPPORT_EMAIL = SUPPORT_EMAIL;
  destroy$: Subject<boolean> = new Subject<boolean>();
  PORTAL_NAME = PORTAL_NAME;
  year: number = (new Date()).getFullYear();
  // step: 1 = formulário, 2 = sucesso, 3 = erro (token inválido/em falta)
  step: number = 0;
  token: string;
  locale: string;
  form: UntypedFormGroup;
  formAllErrors: string = '';
  formErrors = {
    'NewPassword': '',
    'ConfirmNewPassword': ''
  };
  validationMessages: any;
  message: string;

  // Política de password (obtida do backend - CoreDefaultSettings.MinPasswordLenght / StrongPassword)
  minPasswordLength: number = 6;
  strongPasswordEnabled: boolean = false;
  passwordHint: string = '';

  // Configurações
  loginFooterText: string;
  loginFooterUrl: string;

  logoUrl: string = '';

  constructor(private activatedRoute: ActivatedRoute, private router: Router, private renderer: Renderer2,
    private authenticationService: AuthenticationService, private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService, private config: AppConfig) { }

  ngOnInit(): void {
    this.renderer.addClass(document.body, 'texture');

    // Footer
    this.loginFooterText = this.config.getLoginFooterText();
    this.loginFooterUrl = this.config.getLoginFooterUrl();

    this.token = this.activatedRoute.snapshot.queryParams['token'];
    this.locale = this.activatedRoute.snapshot.queryParams['locale'];

    if (!this.token) {
      this.message = 'SET_PASSWORD_INVALID_TOKEN';
      this.step = 3;
      return;
    }

    this.authenticationService.getPasswordPolicy().pipe(takeUntil(this.destroy$)).subscribe(response => {
      const policy = response.ReturnStatus.Successfull && response.ReturnStatus.ReturnObject ? response.ReturnStatus.ReturnObject : {};
      this.minPasswordLength = policy.MinPasswordLength || 6;
      this.strongPasswordEnabled = !!policy.StrongPassword;

      this.translateService.get(['ISEMPTY', 'PASSWORD_DOESNT_MATCH', 'PASSWORD_MIN_LENGTH_ERROR', 'PASSWORD_STRONG_REQUIREMENTS']).subscribe(translations => {
        this.validationMessages = {
          'NewPassword': {
            'required': translations.ISEMPTY,
            'minlength': translations.PASSWORD_MIN_LENGTH_ERROR.replace('{{value}}', this.minPasswordLength),
            'strongPassword': translations.PASSWORD_STRONG_REQUIREMENTS
          },
          'ConfirmNewPassword': {
            'required': translations.ISEMPTY,
            'areEqual': translations.PASSWORD_DOESNT_MATCH
          }
        };
        this.passwordHint = this.strongPasswordEnabled ? translations.PASSWORD_STRONG_REQUIREMENTS : translations.PASSWORD_MIN_LENGTH_ERROR.replace('{{value}}', this.minPasswordLength);

        this.buildForm();
      });
    });
  }

  public ngAfterViewInit(): void {
    App.init();
    let logoImg = this.config.getConfig('LOGO_IMG');
    this.logoUrl = document.getElementsByTagName('base')[0].href + 'assets/configurations/logos/' + logoImg;
  }

  buildForm(): void {
    this.step = 1;

    this.form = this.formBuilder.group({
      'NewPassword': ['', [Validators.required, Validators.minLength(this.minPasswordLength), ModelValidators.strongPassword(this.strongPasswordEnabled)]],
      'ConfirmNewPassword': ['', [Validators.required]]
    }, { validators: (group: UntypedFormGroup) => ModelValidators.areEqual(group, ['NewPassword', 'ConfirmNewPassword']) });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(data => this.onValueChanged(data));
    this.onValueChanged(); // (re)set validation messages now
  }

  onValueChanged(data?: any) {
    if (!this.form) { return; }
    const form = this.form;
    for (const field in this.formErrors) {
      if (this.formErrors.hasOwnProperty(field)) {
        // clear previous error message (if any)
        this.formErrors[field] = '';
        const control = form.get(field);
        if (control && control.dirty && !control.valid) {
          const messages = this.validationMessages[field];
          for (const key in control.errors) {
            if (control.errors.hasOwnProperty(key)) {
              this.formErrors[field] += messages[key] + ' ';
            }
          }
        }
      }
    }
  }

  onSubmit() {
    const newPassword = this.form.value.NewPassword;
    const confirmNewPassword = this.form.value.ConfirmNewPassword;

    this.authenticationService.setPassword(this.token, newPassword, confirmNewPassword, this.locale).pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (response.ReturnStatus.Successfull) {
        this.message = response.ReturnStatus.SuccessMessage || 'SET_PASSWORD_SUCCESS';
        this.step = 2;
        setTimeout(() => this.router.navigate(['/login']), 4000);
      } else {
        response.ReturnStatus.ErrorMessage ?
          this.formAllErrors = response.ReturnStatus.ErrorMessage :
          this.translateService.get('AUTHENTICATION_INVALID').subscribe(translation => this.formAllErrors = translation);
      }
    });
  }

  _onContentChanges() {
    return;
  }

  ngOnDestroy() { }
}
